import React, { useState, useEffect, useRef } from "react";

// --- Global Styles ---
export const GLOBAL_STYLES = `
  :root {
    --brand: #0056D2; --brand-hover: #00419E; --brand-light: #F0F6FF; --brand-focus: rgba(0, 86, 210, 0.4);
    --ink: #1F1F1F; --ink-soft: #575757; --ink-lighter: #757575; --ink70: rgba(31, 31, 31, 0.7);
    --line: #E1E1E1; --bg: #F5F7FA; --card: #FFFFFF;
    --success: #1F7F3E; --success-bg: #EDF7F0; --warning: #A86500; --danger: #D92121;
    --space-4: 4px; --space-8: 8px; --space-12: 12px; --space-16: 16px; --space-24: 24px; --space-32: 32px;
    --radius-sm: 8px; --radius-md: 12px; --radius-lg: 16px;
    --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --font-sans: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: var(--font-sans); background-color: var(--bg); color: var(--ink); -webkit-font-smoothing: antialiased; }
  .app-shell { min-height: 100vh; display: flex; flex-direction: column; }
  .container { max-width: 1280px; margin: 0 auto; padding: 0 var(--space-24); width: 100%; }
  @media (min-width: 768px) { .container { padding: 0 var(--space-32); } }
  .text-heading-xl { font-size: 28px; line-height: 34px; font-weight: 700; letter-spacing: -0.01em; }
  .text-heading-lg { font-size: 22px; line-height: 28px; font-weight: 600; }
  .text-body { font-size: 16px; line-height: 24px; font-weight: 400; }
  .text-label { font-size: 13px; line-height: 18px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; }
  .text-sm { font-size: 14px; line-height: 20px; }
  .bg-var-card { background: var(--card); }
  .animate-slide-up { animation: slideUp 0.3s ease-out forwards; }
  @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  .scroll-lock { overflow: hidden; }
  .grid-responsive { display: grid; grid-template-columns: 1fr; gap: var(--space-24); }
  @media (min-width: 768px) { .grid-responsive { grid-template-columns: repeat(2, 1fr); } }
  @media (min-width: 1024px) { .grid-responsive { grid-template-columns: repeat(3, 1fr); } }
  :focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; border-radius: 4px; }
`;

// --- Icons ---
export const Icons = {
  Check: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
  Close: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  ArrowRight: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>,
  ExternalLink: ({ w=14 }) => <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>,
  Info: ({ w=14 }) => <svg width={w} height={w} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>,
  Logo: () => <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="4" fill="var(--brand)" /><path d="M10 16L14 20L22 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

// --- Components ---
export const AppShell = ({ children }) => <div className="app-shell"><style>{GLOBAL_STYLES}</style>{children}</div>;

export const PageHeader = ({ title, description, actions }) => (
  <header style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "rgba(255, 255, 255, 0.9)", backdropFilter: "blur(8px)", borderBottom: "1px solid var(--line)", padding: "var(--space-16) 0" }}>
    <div className="container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-16)" }}>
        <Icons.Logo />
        <div>
          <h1 className="text-heading-lg" style={{ margin: 0, color: "var(--brand)" }}>{title}</h1>
          <p className="text-sm" style={{ margin: 0, color: "var(--ink-soft)" }}>{description}</p>
        </div>
      </div>
      {actions && <div>{actions}</div>}
    </div>
  </header>
);

export const Card = ({ children, className = "", onClick }) => (
  <div onClick={onClick} className={`transition-all ${className}`} style={{ backgroundColor: "var(--card)", borderRadius: "var(--radius-lg)", border: "1px solid var(--line)", boxShadow: "var(--shadow-sm)", padding: "var(--space-24)", display: "flex", flexDirection: "column", cursor: onClick ? "pointer" : "default", position: "relative" }}
    onMouseEnter={(e) => onClick && (e.currentTarget.style.boxShadow = "var(--shadow-md)") && (e.currentTarget.style.transform = "translateY(-2px)")}
    onMouseLeave={(e) => onClick && (e.currentTarget.style.boxShadow = "var(--shadow-sm)") && (e.currentTarget.style.transform = "translateY(0)")}
  >
    {children}
  </div>
);

export const Button = ({ children, variant = "primary", onClick, icon, ...props }) => {
  const base = { display: "inline-flex", alignItems: "center", justifyContent: 'center', gap: "var(--space-8)", padding: "8px 16px", borderRadius: "var(--radius-sm)", fontSize: "14px", fontWeight: 600, border: "none", cursor: "pointer", transition: "all 0.15s ease-out", textDecoration: "none" };
  const vars = { primary: { backgroundColor: "var(--brand)", color: "white" }, secondary: { backgroundColor: "white", border: "1px solid var(--line)", color: "var(--ink)" }, ghost: { backgroundColor: "transparent", color: "var(--ink-soft)" } };
  return <button style={{ ...base, ...vars[variant] }} onClick={onClick} {...props}>{children}{icon}</button>;
};

export const Pill = ({ label }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "999px", backgroundColor: "var(--brand-light)", color: "var(--brand)", fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", border: "1px solid rgba(0, 86, 210, 0.1)" }}>{label}</span>
);

export const ProgressBar = ({ total, completed }) => {
  const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <div style={{ width: "100%", marginTop: "var(--space-12)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--space-4)" }}>
        <span className="text-label" style={{ fontSize: "11px", color: "var(--ink-soft)" }}>Fairway Action Items</span>
        <span className="text-label" style={{ fontSize: "11px", color: "var(--brand)" }}>{percentage}%</span>
      </div>
      <div style={{ height: "6px", backgroundColor: "var(--bg)", borderRadius: "3px", overflow: "hidden" }}>
        <div style={{ width: `${percentage}%`, height: "100%", backgroundColor: percentage === 100 ? "var(--success)" : "var(--brand)", transition: "width 0.5s ease-out" }} />
      </div>
    </div>
  );
};

export const StatusPill = ({ children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: "99px", fontSize: "12px", fontWeight: 500, backgroundColor: "var(--brand-light)", color: "var(--brand)" }}>{children}</span>
);

export const Badge = ({ variant = "neutral", children }) => {
  const styles = { padding: "2px 8px", borderRadius: "99px", fontSize: "11px", fontWeight: 500, display: "inline-block" };
  const map = {
    neutral: { background: "#F3F4F6", color: "var(--ink)" },
    warn: { background: "#FFF7ED", color: "#9A3412", border: "1px solid #FED7AA" },
    success: { background: "var(--brand-light)", color: "var(--brand)" },
  };
  return <span style={{ ...styles, ...map[variant] }}>{children}</span>;
};

export const Section = ({ index, label, children }) => (
  <section style={{ backgroundColor: "white", border: "1px solid var(--line)", borderRadius: "16px", padding: "20px", boxShadow: "var(--shadow-xs)", marginBottom: "16px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
      <span style={{ width: "24px", height: "24px", borderRadius: "50%", backgroundColor: "var(--brand-light)", color: "var(--brand)", display: "grid", placeItems: "center", fontSize: "13px", fontWeight: 700 }}>{index}</span>
      <h2 style={{ fontSize: "18px", fontWeight: 600, color: "var(--ink)", margin: 0 }}>{label}</h2>
    </div>
    <div style={{ paddingLeft: "36px", color: "var(--ink-soft)", lineHeight: "1.5" }}>{children}</div>
  </section>
);

export const Code = ({ children }) => (
  <code style={{ padding: "2px 6px", borderRadius: "4px", backgroundColor: "#F3F4F6", fontSize: "12px", color: "var(--ink)", fontFamily: "monospace" }}>
    {children}
  </code>
);

export const Disclosure = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: "12px", marginTop: "16px", overflow: "hidden" }}>
      <button 
        onClick={() => setOpen(!open)}
        style={{ width: "100%", textAlign: "left", padding: "12px 16px", background: "white", border: "none", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
      >
        <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--ink)" }}>{title}</span>
        <span style={{ color: "var(--ink-soft)" }}>{open ? "–" : "+"}</span>
      </button>
      {open && <div style={{ padding: "0 16px 16px", fontSize: "14px", color: "var(--ink-soft)", background: "white" }}>{children}</div>}
    </div>
  );
};

export const StandardModal = ({ children, onClose, title }) => (
  <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, backgroundColor: "rgba(31, 31, 31, 0.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "var(--space-16)" }} onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="animate-slide-up" style={{ backgroundColor: "white", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)", width: "100%", maxWidth: "800px", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "var(--space-24)", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <h2 className="text-heading-lg" style={{ margin: 0 }}>{title}</h2>
        <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-soft)", padding: "4px" }}><Icons.Close /></button>
      </div>
      <div style={{ padding: "var(--space-24)", overflowY: "auto" }}>{children}</div>
      <div style={{ padding: "var(--space-16) var(--space-24)", borderTop: "1px solid var(--line)", backgroundColor: "var(--bg)", display: "flex", justifyContent: "flex-end" }}>
        <Button variant="secondary" onClick={onClose}>Done</Button>
      </div>
    </div>
  </div>
);

export const InfoModal = ({ isOpen, onClose, content, actionUrl, actionLabel = "Visit Link" }) => {
  const overlayRef = useRef(null);
  if (!isOpen) return null;
  return (
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.4)" }} onClick={onClose} />
      <div className="animate-slide-up" style={{ position: "relative", backgroundColor: "var(--card)", width: "100%", maxWidth: "720px", borderRadius: "16px", boxShadow: "var(--shadow-md)", border: "1px solid var(--line)", overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <header style={{ position: "sticky", top: 0, backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(4px)", borderBottom: "1px solid var(--line)", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}><StatusPill>✅ Info</StatusPill></div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-soft)", fontSize: "18px" }}>✕</button>
        </header>
        <div style={{ padding: "24px", overflowY: "auto" }}>{content}</div>
        <footer style={{ borderTop: "1px solid var(--line)", padding: "16px 24px", display: "flex", justifyContent: "flex-end", alignItems: "center", backgroundColor: "#F9FAFB" }}>
          <div style={{ display: "flex", gap: "12px" }}>
            <Button variant="secondary" onClick={onClose}>Close</Button>
            {actionUrl && <Button variant="primary" onClick={() => window.open(actionUrl, "_blank")}>{actionLabel} ↗</Button>}
          </div>
        </footer>
      </div>
    </div>
  );
};
